import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EC } from "../../utils/const/ecommerce-colors-const";
import { toImageSrc } from "../../utils/ecommerce/svg-image-src";

const AUTOPLAY_MS = 4500;

export default function HeroCategoryCarousel({ categories = [], onCategorySelect }) {
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    const goTo = useCallback(
        (index) => {
            if (!categories.length) return;
            const next = (index + categories.length) % categories.length;
            setActiveIndex(next);
        },
        [categories.length]
    );

    useEffect(() => {
        if (!categories.length || isPaused) return undefined;

        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (prefersReducedMotion) return undefined;

        const timer = setInterval(() => {
            setActiveIndex((current) => (current + 1) % categories.length);
        }, AUTOPLAY_MS);

        return () => clearInterval(timer);
    }, [categories.length, isPaused]);

    if (!categories.length) return null;

    const activeCategory = categories[activeIndex];
    // console.log("activeCategory");
    // console.log(activeCategory);
    const activeLabel = t(`${activeCategory.descripcion}`);

    const handleSelect = (category) => {
        onCategorySelect?.(category);
    };

    return (
        <div
            className={`group relative overflow-hidden rounded-3xl border border-slate-200/80 shadow-2xl ${EC.hero.imageRing}`}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
        >
            <div className="relative aspect-[4/3] lg:aspect-[5/4] w-full overflow-hidden bg-slate-100">
                <div
                    className="flex h-full transition-transform duration-700 ease-in-out"
                    style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                >
                    {categories.map((category) => {
                        const label = t(`${category.descripcion}`);
                        return (
                            <button
                                key={category.id}
                                type="button"
                                onClick={() => handleSelect(category)}
                                className="relative h-full w-full shrink-0 text-left"
                                aria-label={label}
                            >
                                <img
                                    src={toImageSrc(category.svg)}
                                    alt={label}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/20 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 pr-16 sm:pr-24">
                                    {/* <span className="text-4xl sm:text-5xl mb-2 block" aria-hidden>
                                        {category.icon}
                                    </span> */}
                                    <p className="text-xs font-semibold uppercase tracking-widest text-ct-primary-200 mb-1">
                                        {t("ct_hero_carousel_label")}
                                    </p>
                                    <h3 className="text-2xl sm:text-3xl font-extrabold text-white">{label}</h3>
                                    <p className="mt-2 text-sm text-slate-200 hidden sm:block">
                                        {t("ct_hero_carousel_hint")}
                                    </p>
                                </div>
                            </button>
                        );
                    })}
                </div>

                <button
                    type="button"
                    onClick={() => goTo(activeIndex - 1)}
                    className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow-md opacity-100 sm:opacity-0 transition hover:bg-white group-hover:opacity-100 focus:opacity-100 sm:left-4 sm:h-11 sm:w-11"
                    aria-label={t("ct_gallery_prev")}
                >
                    ‹
                </button>
                <button
                    type="button"
                    onClick={() => goTo(activeIndex + 1)}
                    className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow-md opacity-100 sm:opacity-0 transition hover:bg-white group-hover:opacity-100 focus:opacity-100 sm:right-4 sm:h-11 sm:w-11"
                    aria-label={t("ct_gallery_next")}
                >
                    ›
                </button>
            </div>

            <div
                className="absolute bottom-14 left-1/2 z-10 flex -translate-x-1/2 gap-1.5 sm:bottom-16"
                role="tablist"
                aria-label={t("ct_hero_carousel_dots")}
            >
                {categories.map((category, index) => (
                    <button
                        key={category.id}
                        type="button"
                        role="tab"
                        aria-selected={index === activeIndex}
                        aria-label={t(`${category.id}`)}
                        onClick={() => setActiveIndex(index)}
                        className={`rounded-full transition-all ${index === activeIndex ? EC.gallery.dotActive : EC.gallery.dotInactive
                            }`}
                    />
                ))}
            </div>

            <p className="sr-only" aria-live="polite">
                {activeLabel}
            </p>
        </div>
    );
}
